import { useNavigate } from 'react-router-dom';

export default function AIRoboticsLab() {
  const navigate = useNavigate();

  const goToConsultation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    sessionStorage.setItem('scrollToSection', 'contact');
    navigate('/');
  };
  const features = [
    {
      icon: 'ri-robot-2-line',
      title: 'Advanced Robotics Kits',
      description: 'Industry-standard robotics platforms for hands-on learning'
    },
    {
      icon: 'ri-brain-line',
      title: 'AI Programming Tools',
      description: 'Machine learning platforms and AI development environments'
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'Coding Platforms',
      description: 'Python, JavaScript, and block-based programming tools'
    },
    {
      icon: 'ri-cpu-line',
      title: 'IoT Devices',
      description: 'Sensors, microcontrollers, and smart device kits'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'International Certifications',
      description: 'Microsoft, Google, and STEM.org recognized certificates'
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance Support',
      description: 'Annual maintenance and equipment replacement included'
    }
  ];

  const technologies = [
    { name: 'Robotics', icon: 'ri-robot-line', color: 'from-blue-500 to-blue-600' },
    { name: 'Artificial Intelligence', icon: 'ri-brain-line', color: 'from-purple-500 to-purple-600' },
    { name: 'Machine Learning', icon: 'ri-line-chart-line', color: 'from-green-500 to-green-600' },
    { name: 'IoT & Sensors', icon: 'ri-sensor-line', color: 'from-orange-500 to-orange-600' },
    { name: '3D Printing', icon: 'ri-printer-line', color: 'from-pink-500 to-pink-600' },
    { name: 'Drone Technology', icon: 'ri-flight-takeoff-line', color: 'from-teal-500 to-teal-600' }
  ];

  return (
    <section id="ai-robotics" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Advanced%20AI%20and%20robotics%20laboratory%20with%20students%20programming%20robotic%20arms%2C%20computer%20screens%20showing%20code%2C%20modern%20technology%20classroom%20with%20blue%20accent%20lighting%2C%20futuristic%20educational%20setting%20with%20electronic%20components&width=800&height=600&seq=ai-robotics-detail-001&orientation=landscape"
                alt="AI & Robotics Lab"
                className="w-full h-[500px] object-cover object-top"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <div className="inline-block px-4 py-2 bg-purple-500/20 text-purple-300 text-sm font-semibold uppercase tracking-wider rounded-full mb-4 border border-purple-500/30">
              Future-Ready Technology
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
              AI &amp; Robotics Lab
            </h2>
            <p className="text-base text-gray-300 mb-7 leading-relaxed">
              Prepare students for the future with cutting-edge AI, robotics, and IoT technologies that develop critical thinking and innovation skills.
            </p>

            <div className="space-y-3 mb-7">
              {[
                'Industry-standard robotics platforms and AI tools',
                'Hands-on projects in machine learning and automation',
                'IoT device programming and smart systems',
                'International certification programs',
                'Competition preparation and mentorship'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-400 text-xl flex-shrink-0"></i>
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-purple-500 rounded-2xl p-5 text-white mb-5">
              <div className="text-xs mb-1">Investment Range</div>
              <div className="text-3xl font-bold mb-1">₹12-25 Lakhs</div>
              <div className="text-xs text-purple-100">Includes equipment, software licenses, and training</div>
            </div>

            <a
              href="/#contact"
              onClick={goToConsultation}
              className="inline-block px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              Get Custom Quote
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 hover:bg-white/20 transition-all"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center mb-4">
                <i className={`${feature.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-300 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Technologies */}
        <div className="bg-white rounded-3xl p-12">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-7">
            Technologies Covered
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, idx) => (
              <div
                key={idx}
                className="text-center"
              >
                <div className={`w-20 h-20 mx-auto bg-gradient-to-br ${tech.color} rounded-xl flex items-center justify-center mb-3 shadow-lg hover:scale-110 transition-all`}>
                  <i className={`${tech.icon} text-4xl text-white`}></i>
                </div>
                <p className="font-semibold text-gray-900 text-sm">{tech.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
